import React, { useCallback } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  InputBase,
  Tooltip,
  Typography,
} from '@mui/material';
import AddRoundedIcon from '@mui/icons-material/AddRounded';
import SendRoundedIcon from '@mui/icons-material/SendRounded';
import MicNoneOutlinedIcon from '@mui/icons-material/MicNoneOutlined';
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import PlayArrowRoundedIcon from '@mui/icons-material/PlayArrowRounded';
import { IconButton } from '../../../../components/ui';
import ComposerAttachMenu from './ComposerAttachMenu';
import VoiceNoteDialog from './VoiceNoteDialog';
import useInterveneSx from '../../hooks/useInterveneSx';
import {
  canComposeFreeForm,
  isComposerExpired,
  isComposerIdle,
} from '../../utils/composerMode';
import { getAttachmentAccept } from '../../utils/attachmentConstraints';

export default function Composer({
  draftMessage,
  onDraftChange,
  isSending,
  composerMode = 'active',
  canUseChat = true,
  isOptedOut = false,
  onSendTemplate,
  onStartSession,
  composer,
}) {
  const styles = useInterveneSx().composer;
  const {
    fileInputRef,
    attachButtonRef,
    selectedFile,
    fileError,
    clearFileError,
    voiceDialogOpen,
    attachMenuOpen,
    openVoiceDialog,
    closeVoiceDialog,
    openAttachMenu,
    closeAttachMenu,
    handleFileSelect,
    openAttachmentPicker,
    clearSelectedFile,
    handleSend,
    handleKeyDown,
    handleVoiceComplete,
  } = composer;

  const handleAttachSelect = useCallback(
    (itemId) => {
      closeAttachMenu();
      clearFileError?.();

      if (itemId === 'template') {
        onSendTemplate?.();
        return;
      }

      if (itemId === 'document' || itemId === 'image' || itemId === 'video') {
        openAttachmentPicker(itemId);
        return;
      }

      if (itemId === 'voice') {
        openVoiceDialog();
      }
    },
    [
      clearFileError,
      closeAttachMenu,
      onSendTemplate,
      openAttachmentPicker,
      openVoiceDialog,
    ]
  );

  const toggleAttachMenu = useCallback(() => {
    if (attachMenuOpen) {
      closeAttachMenu();
    } else {
      openAttachMenu();
    }
  }, [attachMenuOpen, closeAttachMenu, openAttachMenu]);

  if (!canUseChat) {
    return (
      <Box sx={styles.outer}>
        <Alert severity="info" sx={{ py: 0.5, px: 1.25, fontSize: '0.75rem' }}>
          Live Chat permission is required to send messages.
        </Alert>
      </Box>
    );
  }

  if (isOptedOut) {
    return (
      <Box sx={styles.outer}>
        <Alert severity="error" sx={{ py: 0.75, px: 1.5, fontSize: '0.8125rem', fontWeight: 500 }}>
          ⚠️ Contact has opted out of WhatsApp messages. Intervene session and messaging are
          disabled.
        </Alert>
      </Box>
    );
  }

  const isExpired = isComposerExpired(composerMode);
  const isIdle = isComposerIdle(composerMode);
  const canFreeForm = canComposeFreeForm(composerMode);
  const canSend = !isSending && canFreeForm && (draftMessage.trim() || selectedFile);
  const hasDraft = Boolean(draftMessage.trim() || selectedFile);

  return (
    <Box sx={styles.outer}>
      <ComposerAttachMenu
        anchorEl={attachButtonRef.current}
        open={attachMenuOpen}
        onClose={closeAttachMenu}
        composerMode={composerMode}
        onSelect={handleAttachSelect}
      />

      {fileError && (
        <Alert
          severity="warning"
          onClose={clearFileError}
          sx={{ py: 0.25, px: 1.25, fontSize: '0.75rem' }}
        >
          {fileError}
        </Alert>
      )}

      {selectedFile && canFreeForm && (
        <Box sx={styles.fileChip}>
          <Typography
            variant="caption"
            noWrap
            sx={{
              maxWidth: 240,
              fontWeight: 'var(--font-weight-medium)',
              fontSize: 'var(--font-size-xs)',
            }}
          >
            {selectedFile.name}
          </Typography>
          <Chip
            label="Remove"
            size="small"
            onClick={clearSelectedFile}
            sx={{ cursor: 'pointer', height: 22, fontSize: 'var(--font-size-xs)' }}
          />
        </Box>
      )}

      <Box sx={[styles.pill, (isExpired || isIdle) && styles.pillExpired , { alignItems: styles.pillExpired.alignItems }]}>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          style={{ display: 'none' }}
          accept={getAttachmentAccept('document')}
        />

        <Tooltip title={isExpired ? 'Send template' : isIdle ? 'Send template without starting the session' : 'Attach'}>
          <span>
            <IconButton
              ref={attachButtonRef}
              size="sm"
              variant={isExpired || isIdle ? 'soft' : 'ghost'}
              aria-label={isExpired || isIdle ? 'Send template' : 'Attach file'}
              onClick={toggleAttachMenu}
              sx={[
                styles.attachButton,
                isExpired && styles.attachButtonExpired,
                attachMenuOpen && styles.attachButtonOpen,
              ]}
            >
              <AddRoundedIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </span>
        </Tooltip>

        {isExpired ? (
          <Box sx={styles.expiredState}>
            <ScheduleOutlinedIcon sx={styles.expiredIcon} />
            <Box sx={styles.expiredCopy}>
              <Typography component="span" sx={styles.expiredTitle}>
                Session expired
              </Typography>
              <Typography component="span" sx={styles.expiredHint}>
                Tap + to send a template
              </Typography>
            </Box>
          </Box>
        ) : isIdle ? (
          <Box sx={styles.expiredState}>
            <PlayArrowRoundedIcon sx={styles.expiredIcon} />
            <Box sx={styles.expiredCopy}>
              <Typography component="span" sx={styles.expiredTitle}>
                Ready to intervene
              </Typography>
              <Typography component="span" sx={styles.expiredHint}>
                Click Start to take over this chat
              </Typography>
            </Box>
            {onStartSession && (
              <Button
                size="small"
                variant="contained"
                onClick={onStartSession}
                sx={styles.idleCta}
              >
                Start
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{...styles.inputArea , '& .MuiInputBase-root': { lineHeight: 1.6 }, '& .MuiInputBase-input': { height: '21px', minHeight: '21px' }}}>
            <InputBase
              fullWidth
              multiline
              maxRows={4}
              aria-label="Message composer"
              placeholder="Type a message"
              value={draftMessage}
              onChange={(event) => onDraftChange(event.target.value)}
              onKeyDown={handleKeyDown}
            />
          </Box>
        )}

        {/* {canFreeForm &&
          (hasDraft ? (
            <Tooltip title="Send">
              <span>
                <IconButton
                  size="sm"
                  aria-label="Send message"
                  onClick={handleSend}
                  disabled={!canSend}
                  loading={isSending}
                  variant="primary"
                  sx={styles.sendButton}
                >
                  <SendRoundedIcon sx={{ fontSize: 17 }} />
                </IconButton>
              </span>
            </Tooltip>
          ) : (
            // <Tooltip title="Voice note">
            //   <span>
            //     <IconButton
            //       size="sm"
            //       aria-label="Record voice note"
            //       onClick={openVoiceDialog}
            //       sx={styles.actionButton}
            //     >
            //       <MicNoneOutlinedIcon sx={{ fontSize: 19 }} />
            //     </IconButton>
            //   </span>
            // </Tooltip>
          ))} */}
        {canFreeForm && hasDraft && (
          <Tooltip title="Send">
            <span>
              <IconButton
                size="sm"
                aria-label="Send message"
                onClick={handleSend}
                disabled={!canSend}
                loading={isSending}
                variant="primary"
                sx={styles.sendButton}
              >
                <SendRoundedIcon sx={{ fontSize: 17 }} />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Box>

      {/* <VoiceNoteDialog
        open={voiceDialogOpen}
        onClose={closeVoiceDialog}
        onRecordingComplete={handleVoiceComplete}
      /> */}
    </Box>
  );
}
